import { ACCOUNT_PLAYLIST_COOLDOWN_MS, PLAYLIST_RULE_SCOPE_ACCOUNT } from 'constants/playlists';
import { createPlaylistRule, updatePlaylistRule } from 'services/playlists';

type AccountPlaylistState = {
  is_account_playlist: boolean;
  cooldown_ends_at: number | null;
};

const stateByPlaylist: Record<string, AccountPlaylistState> = {};

export const getAccountPlaylistCooldownSecondsRemaining = (playlistId?: string): number => {
  if (!playlistId) return 0;
  const state = stateByPlaylist[playlistId];
  const cooldownEndsAt = state?.cooldown_ends_at;
  if (!cooldownEndsAt) return 0;
  const remainingSeconds = Math.ceil((cooldownEndsAt - Date.now()) / 1000);
  return remainingSeconds > 0 ? remainingSeconds : 0;
};

// Shared store for account-playlist toggle state across instances.
export const createAccountPlaylistToggleStore = () => {
  const ensureState = (
    playlistId: string,
    seedState?: Partial<AccountPlaylistState>
  ): AccountPlaylistState => {
    if (!stateByPlaylist[playlistId]) {
      stateByPlaylist[playlistId] = {
        is_account_playlist: Boolean(seedState?.is_account_playlist),
        cooldown_ends_at: seedState?.cooldown_ends_at ?? null,
      };
    } else if (typeof seedState?.is_account_playlist === 'boolean') {
      const hasCooldown =
        stateByPlaylist[playlistId].cooldown_ends_at &&
        stateByPlaylist[playlistId].cooldown_ends_at > Date.now();
      if (
        !hasCooldown &&
        stateByPlaylist[playlistId].is_account_playlist !== seedState.is_account_playlist
      ) {
        stateByPlaylist[playlistId].is_account_playlist = seedState.is_account_playlist;
      }
    }
    return stateByPlaylist[playlistId];
  };

  return {
    async getState(
      playlistId: string,
      seedState?: Partial<AccountPlaylistState>
    ): Promise<AccountPlaylistState> {
      const currentState = ensureState(playlistId, seedState);
      return { ...currentState };
    },
    async setState({
      playlistId,
      enabled,
    }: {
      playlistId: string;
      enabled: boolean;
    }): Promise<AccountPlaylistState> {
      const currentState = ensureState(playlistId);
      if (currentState.cooldown_ends_at && currentState.cooldown_ends_at > Date.now()) {
        throw new Error('Cooldown active');
      }

      const response = enabled
        ? await createPlaylistRule({
            playlist_id: playlistId,
            scope: PLAYLIST_RULE_SCOPE_ACCOUNT,
          })
        : await updatePlaylistRule({
            playlist_id: playlistId,
            scope: PLAYLIST_RULE_SCOPE_ACCOUNT,
          });

      const nextIsAccountPlaylist =
        typeof response?.data?.is_active === 'boolean' ? response.data.is_active : enabled;
      const cooldown_ends_at = Date.now() + ACCOUNT_PLAYLIST_COOLDOWN_MS;
      stateByPlaylist[playlistId] = {
        is_account_playlist: nextIsAccountPlaylist,
        cooldown_ends_at,
      };

      return { ...stateByPlaylist[playlistId] };
    },
  };
};

export type { AccountPlaylistState };
